import { useState } from 'react';
import { Pencil, UserMinus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/composed/DataTable/DataTable';
import { StatusBadge } from '@/components/composed/StatusBadge';
import { ConfirmDialog } from '@/components/composed/ConfirmDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { type TableFeatures } from '@/components/composed/DataTable/table-features';
import { canAdministerUsers } from '@/lib/permissions';
import { formatLastLogin } from '@/lib/format';
import { useAuthStore } from '@/lib/session/auth-store';
import type { OrgKind, User, UserRole } from '@/lib/api/domains/users';
import { AddUserDialog } from './AddUserDialog';
import { UserEditorDialog } from './UserEditorDialog';
import { entityUsersQuery } from '../api/user-queries';
import { useDeactivateUser } from '../api/user-mutations';
import { permissionSetLabel } from '../permission-set-options';

/*
 * The entity-record Users section (F02 R20, R17, R31). Built for task 6.2 to
 * satisfy the task-6.1 behavioural tests in EntityUsersSection.test.tsx:
 *
 *   - AC-5f7370f6 — add/change/deactivate a user FROM THE ORGANIZATION'S OWN
 *     RECORD without leaving it: the section lives on the entity detail page
 *     and carries the add affordance (reusing the task-5 AddUserDialog) and the
 *     per-row edit/deactivate affordances, so the operator never leaves the
 *     record to manage its users. The dialog opens IN PLACE; nothing navigates.
 *   - AC-1de5d1a7 — the list shows, for each account: the person's name, their
 *     email, the role, and whether the account is active. (The permission-set
 *     column is task 8's AC-b31e4456 and the last-login column is task 9's
 *     AC-fcf5853c — NOT built here; only the fields the 6.1 test asserts now.)
 *   - AC-d9396c36 / AC-58d352fb — the add/edit/deactivate actions are WITHHELD
 *     from a person who does not hold the is_administrator privilege (the
 *     courtesy half; `canAdministerUsers`). A build that only hides is worse
 *     than one that only refuses — the server (task 1) is the authority and
 *     refuses regardless; this only withholds the controls.
 *   - AC-1a35dc54 — a person who is NOT an Administrator READS the list within
 *     the scope the session already carries and is OFFERED NO WAY to change it:
 *     the list renders, no add/edit/deactivate affordance appears. The list is
 *     exactly what the scoped read returns — the section renders the server's
 *     rows and NEVER widens, filters, or reaches beyond them.
 *
 * Gating is the one `canAdministerUsers` predicate from lib/permissions (never
 * a role-string compare). The scoped list comes from `entityUsersQuery`, which
 * reads the already-scoped `GET /entities/:id/users` — the frontend renders it
 * as-is.
 *
 * Seams left for later tasks: the full user editor behind "Edit" is task 7; the
 * permission-set column is task 8; the last-login column is task 9. Here the
 * edit/deactivate affordances only need to be present and wired to a seam.
 */

const ROLE_LABELS: Record<UserRole, string> = {
  tpabilling: 'TPA Billing',
  client: 'Client',
};

export function EntityUsersSection({
  entityId,
  orgKind,
}: {
  /** The organization whose user accounts this section manages. */
  entityId: string;
  /** The organization's kind, which decides the roles the add chooser offers. */
  orgKind: OrgKind;
}): React.ReactElement {
  // The session is read from the derived auth store so the gate is the same one
  // every surface reads (guards, nav, affordances). The server is the authority
  // (task 1); this is the courtesy half only.
  const session = useAuthStore((s) => s.session);
  const canManage = canAdministerUsers(session);

  // The already-scoped list. The section renders exactly these rows and never
  // widens them (AC-1a35dc54).
  const { data, isLoading, isError } = useQuery(entityUsersQuery(entityId));
  const rows = data ?? [];

  // The count on the list summary counts ACTIVE accounts only — a deactivated
  // row is still LISTED, just not counted (AC-aad06fad). Counting active-only is
  // not the same as hiding the inactive row.
  const activeCount = rows.filter((u) => u.isActive).length;

  // The add affordance opens the dialog IN PLACE — managing users never leaves
  // the record (AC-5f7370f6).
  const [addOpen, setAddOpen] = useState(false);

  // The account being edited (task 7 editor, opened in place) and the account
  // queued for deactivation (its confirmation names the person and states the
  // effect before anything happens — AC-ce795f57). Both are transient local
  // state; managing users never leaves the record.
  const [editing, setEditing] = useState<User | null>(null);
  const [deactivating, setDeactivating] = useState<User | null>(null);

  const deactivateUser = useDeactivateUser(entityId);

  const columns: Array<ColumnDef<TableFeatures, User>> = [
    {
      id: 'displayName',
      header: 'Name',
      accessorKey: 'displayName',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.displayName}</span>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      accessorKey: 'email',
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.original.email}</span>
      ),
    },
    {
      id: 'role',
      header: 'Role',
      accessorKey: 'role',
      cell: ({ row }) => <span>{ROLE_LABELS[row.original.role]}</span>,
    },
    {
      // The set the account holds, shown WITHOUT opening the account
      // (AC-b31e4456): a null set (baseline alone) renders a readable "None",
      // never a blank cell. Read via the set-label helper (not the Role
      // column), so the central and entity-record lists answer the set the same
      // way. Part of the read offered to every reader, Viewer included
      // (AC-1a35dc54), not an administrator-only affordance.
      id: 'permissionSet',
      header: 'Permission set',
      accessorKey: 'permissionSet',
      cell: ({ row }) => (
        <span>{permissionSetLabel(row.original.permissionSet)}</span>
      ),
    },
    {
      // When the account last signed in, in ADDITION to the fields already on
      // the list (AC-fcf5853c): a never-signed-in account renders the literal
      // "Never" (AC-fc41f232), a non-null instant renders date + time
      // (AC-5608aa1d) in US Eastern for every reader (AC-51bebe6e). Read via
      // `formatLastLogin` from lib/format — no raw Date in JSX (AGENTS.md).
      // Part of the read offered to every reader, Viewer included (AC-1a35dc54).
      id: 'lastLoginOn',
      header: 'Last login',
      accessorKey: 'lastLoginOn',
      cell: ({ row }) => (
        <span>{formatLastLogin(row.original.lastLoginOn)}</span>
      ),
    },
    {
      id: 'isActive',
      header: 'Status',
      accessorKey: 'isActive',
      cell: ({ row }) => (
        <StatusBadge
          label={row.original.isActive ? 'Active' : 'Inactive'}
          tone={row.original.isActive ? 'success' : 'neutral'}
        />
      ),
    },
  ];

  return (
    // A labelled landmark region so the section's content is contained by a
    // landmark (a11y: axe "region"). The heading names the region.
    <section className="flex flex-col gap-4" aria-label="Users">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Users</h3>
          <p
            className="text-xs text-muted-foreground"
            data-testid="entity-users-active-count"
          >
            {activeCount} active {activeCount === 1 ? 'account' : 'accounts'} at
            this organization.
          </p>
        </div>
        {/* The add affordance is withheld from a non-privileged person
            (AC-d9396c36); a privileged person adds a user from the record
            without leaving it (AC-5f7370f6). */}
        {canManage ? (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            Add user
          </Button>
        ) : null}
      </div>

      <DataTable<User>
        columns={columns}
        data={rows}
        getRowId={(row) => row.id}
        tableId="entity-users"
        isLoading={isLoading}
        isError={isError}
        errorMessage="Failed to load users."
        emptyTitle="No users"
        emptyDescription="No user accounts have been added for this organization."
        // Per-row change/deactivate affordances, offered only to a privileged
        // person (AC-5f7370f6; withheld otherwise, AC-d9396c36 / AC-1a35dc54).
        // Edit is wired to the task-7 editor seam; Deactivate to the task-7
        // deactivate seam. The active account can be deactivated.
        actions={
          canManage
            ? (row) => (
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Edit ${row.displayName}`}
                    // Open the task-7 editor for this row, in place — the
                    // operator never leaves the record (AC-5f7370f6). The editor
                    // reads the account against current state (AC-cd9143ad).
                    onClick={() => setEditing(row)}
                  >
                    <Pencil className="size-4" />
                    <span>Edit</span>
                  </Button>
                  {row.isActive ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Deactivate ${row.displayName}`}
                      // Deactivation goes through a confirmation that NAMES the
                      // person and STATES the effect before anything happens
                      // (AC-ce795f57); offered only on an active row
                      // (AC-74de7113). The confirm fires the deactivate on
                      // accept only.
                      onClick={() => setDeactivating(row)}
                    >
                      <UserMinus className="size-4" />
                      <span>Deactivate</span>
                    </Button>
                  ) : null}
                </div>
              )
            : undefined
        }
      />

      {/* Add happens in a dialog ON THE RECORD — the operator never leaves the
          entity detail page (AC-5f7370f6). Rendered only when the privilege is
          held so a non-privileged person has no path to it at all. */}
      {canManage ? (
        <AddUserDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          entityId={entityId}
          orgKind={orgKind}
        />
      ) : null}

      {/* The user editor, opened IN PLACE for the row being edited (task 7). It
          reads the account against current state (AC-cd9143ad) and carries its
          own deactivate/reactivate affordances; here it is the "Edit" path. */}
      {canManage && editing ? (
        <UserEditorDialog
          open
          onOpenChange={(next) => {
            if (!next) setEditing(null);
          }}
          entityId={entityId}
          userId={editing.id}
          orgKind={orgKind}
        />
      ) : null}

      {/* The deactivation confirmation from the row (AC-ce795f57): it NAMES THE
          PERSON and STATES WHAT DEACTIVATION DOES before anything happens, and
          the deactivate fires only on accept, targeting the account by id (the
          name is shown here, never sent — PII stays out of requests).
          Destructive styling. */}
      {canManage && deactivating ? (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) setDeactivating(null);
          }}
          title="Deactivate user"
          description={
            <span className="flex flex-col gap-2">
              <span>{`Deactivate ${deactivating.displayName}? They will no longer be able to sign in or access this organization. The account is kept and can be reactivated later.`}</span>
              {/* "Nothing fails silently" (finding 11): a refused deactivate is
                  surfaced where the operator works and the confirm stays open
                  so it can be retried, symmetric with the save paths. One
                  generic text regardless of cause; the 401/403 interceptor is
                  the backstop, never a component-level status check. */}
              {deactivateUser.isError ? (
                <Alert variant="destructive" role="alert">
                  <AlertDescription>
                    That account could not be deactivated. Please try again.
                  </AlertDescription>
                </Alert>
              ) : null}
            </span>
          }
          confirmLabel="Deactivate"
          cancelLabel="Cancel"
          destructive
          onConfirm={async () => {
            // mutateAsync REJECTS on failure, so ConfirmDialog keeps the dialog
            // open (it only closes on a resolved confirm); the failure then
            // renders in the description above. On success the dialog closes.
            await deactivateUser.mutateAsync(deactivating.id);
          }}
        />
      ) : null}
    </section>
  );
}
